import { h, clear, toast, showModal, formatDate, statusBadge, confirmModal, fold } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Plus, Eye, Pencil, Check, Trash2 } from 'lucide';
import { BranchAPI } from '../services/endpoints.js';
import { CurrentUser } from '../services/api.js';
import { validateForm, showFieldErrors, required, optionalEmail, minLen, maxLen } from '../utils/validators.js';
import { Tooltip } from '../components/tooltip.js';
import { PageTabs } from '../components/ui.js';
import { SkeletonTable } from '../components/skeleton.js';

const DEPARTMENTS = [
  { value: 'amazonas', label: 'Amazonas' },
  { value: 'antioquia', label: 'Antioquia' },
  { value: 'arauca', label: 'Arauca' },
  { value: 'atlantico', label: 'Atlántico' },
  { value: 'bolivar', label: 'Bolívar' },
  { value: 'boyaca', label: 'Boyacá' },
  { value: 'caldas', label: 'Caldas' },
  { value: 'caqueta', label: 'Caquetá' },
  { value: 'casanare', label: 'Casanare' },
  { value: 'cauca', label: 'Cauca' },
  { value: 'cesar', label: 'Cesar' },
  { value: 'choco', label: 'Chocó' },
  { value: 'cordoba', label: 'Córdoba' },
  { value: 'cundinamarca', label: 'Cundinamarca' },
  { value: 'guainia', label: 'Guainía' },
  { value: 'guaviare', label: 'Guaviare' },
  { value: 'huila', label: 'Huila' },
  { value: 'laguajira', label: 'La Guajira' },
  { value: 'magdalena', label: 'Magdalena' },
  { value: 'meta', label: 'Meta' },
  { value: 'narino', label: 'Nariño' },
  { value: 'nortedesantander', label: 'Norte de Santander' },
  { value: 'putumayo', label: 'Putumayo' },
  { value: 'quindio', label: 'Quindío' },
  { value: 'risaralda', label: 'Risaralda' },
  { value: 'sanandres', label: 'San Andrés y Providencia' },
  { value: 'santander', label: 'Santander' },
  { value: 'sucre', label: 'Sucre' },
  { value: 'tolima', label: 'Tolima' },
  { value: 'valle', label: 'Valle del Cauca' },
  { value: 'vaupes', label: 'Vaupés' },
  { value: 'vichada', label: 'Vichada' },
  { value: 'international', label: 'Internacional' },
];

const DEPARTMENT_LABELS = Object.fromEntries(DEPARTMENTS.map((d) => [d.value, d.label]));

const branchRules = {
  name:       [(v) => required(v, 'El nombre'), minLen(2, 'El nombre'), maxLen(100, 'El nombre')],
  department: [(v) => required(v, 'El departamento')],
  address:    [maxLen(200, 'La dirección')],
  phone:      [maxLen(30, 'El teléfono')],
  email:      [optionalEmail],
};

export const BranchesPage = async (user) => {
  const view = document.getElementById('view');
  clear(view);
  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Sucursales'),
    user.role === 'admin' ? h('button', { class: 'btn', onclick: () => openCreate() }, h('span', { class: 'btn-icon', html: iconSvg(Plus, 16) }), 'Nueva sucursal') : null
  ));

  let statusFilter = 'active';

  const filter = h('div', { class: 'filter-bar mb-16' },
    h('input', { type: 'search', name: 'q', 'aria-label': 'Buscar sucursales', placeholder: 'Buscar por nombre, dirección…', style: { minWidth: '220px' }, oninput: applyFilters }),
    h('span', { class: 'filter-label' }, 'Estado'),
    PageTabs({
      tabs: [
        { id: '', label: 'Todas' },
        { id: 'active', label: 'Activo' },
        { id: 'inactive', label: 'Inactivo' },
      ],
      activeTab: statusFilter,
      onTabChange: (id) => { statusFilter = id; applyFilters(); },
    }),
  );
  view.appendChild(filter);

  const card = h('div', { class: 'card' });
  const tableWrap = h('div', {});

  // Show skeleton while loading
  const skeleton = h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
    h('thead', {}, h('tr', {},
      h('th', {}, 'Sucursal'),
      h('th', {}, 'Departamento'),
      h('th', {}, 'Dirección'),
      h('th', {}, 'Teléfono'),
      h('th', {}, 'Correo'),
      h('th', {}, 'Estado'),
      h('th', {}, ''),
    )),
    SkeletonTable(5, 7),
  ));
  card.appendChild(skeleton);
  view.appendChild(card);

  let allBranches = [];

  try {
    const res = await BranchAPI.list({ include_inactive: user.role === 'admin' ? '1' : '0' });
    allBranches = res.data || [];
    skeleton.replaceWith(tableWrap);
    applyFilters();
  } catch (err) {
    skeleton.remove();
    toast(err.message, 'error');
  }

  async function refresh() {
    try {
      const res = await BranchAPI.list({ include_inactive: user.role === 'admin' ? '1' : '0' });
      allBranches = res.data || [];
      applyFilters();
    } catch (err) {
      toast(err.message, 'error');
    }
  }

  function applyFilters() {
    const q = filter.querySelector('[name="q"]').value.toLowerCase().trim();
    const foldedQ = fold(q);
    let items = allBranches;
    if (q) {
      items = items.filter((b) =>
        [b.name, b.address, b.phone, b.email].some((v) => v && fold(v).includes(foldedQ))
      );
    }
    if (statusFilter === 'active') items = items.filter((b) => b.is_active);
    else if (statusFilter === 'inactive') items = items.filter((b) => !b.is_active);
    renderTable(tableWrap, items, user);
  }

  function renderTable(parent, items, user) {
    clear(parent);
    if (!items.length) {
      parent.appendChild(h('div', { class: 'table-empty' }, 'No hay sucursales que coincidan con los filtros.'));
      return;
    }
    const table = h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Nombre'),
        h('th', {}, 'Departamento'),
        h('th', {}, 'Dirección'),
        h('th', {}, 'Teléfono'),
        h('th', {}, 'Correo electrónico'),
        h('th', {}, 'Estado'),
        h('th', {}, ''),
      )),
      h('tbody', {}, ...items.map((b) => h('tr', {},
        h('td', {}, b.name),
        h('td', {}, DEPARTMENT_LABELS[b.department] || b.department || '-'),
        h('td', {}, b.address || '-'),
        h('td', {}, b.phone || '-'),
        h('td', {}, b.email || '-'),
        h('td', {}, h('span', { class: `badge ${b.is_active ? 'active' : 'inactive'}` }, b.is_active ? 'Activo' : 'Inactivo')),
        h('td', {},
          h('div', { class: 'actions-cell' },
            Tooltip('Ver detalle', h('button', { class: 'btn-icon-only btn-view', onclick: () => openDetail(b) }, h('span', { html: iconSvg(Eye, 14) }))),
            user.role === 'admin' ? Tooltip('Editar sucursal', h('button', { class: 'btn-icon-only btn-edit', onclick: () => openEdit(b) }, h('span', { html: iconSvg(Pencil, 14) }))) : null,
          )
        )
      )))
    );
    parent.appendChild(h('div', { class: 'table-wrap' }, table));
  }

  function openCreate() {
    const form = h('div', { class: 'form-create' },
      h('h3', {}, 'Nueva sucursal'),
      h('p', { class: 'muted text-sm' }, 'Registra una nueva sede o sucursal del SENA.'),
      h('div', { class: 'form-section' },
        h('label', {}, 'Nombre'),
        h('input', { name: 'name', autocomplete: 'organization', required: true }),
        h('label', {}, 'Departamento'),
        h('select', { name: 'department', required: true },
          h('option', { value: '' }, 'Seleccionar…'),
          ...DEPARTMENTS.map((d) => h('option', { value: d.value }, d.label)),
        ),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Dirección'),
            h('input', { name: 'address' }),
          ),
          h('div', {},
            h('label', {}, 'Teléfono'),
            h('input', { name: 'phone', type: 'tel', inputmode: 'numeric' }),
          ),
        ),
        h('label', {}, 'Correo electrónico'),
        h('input', { name: 'email', type: 'email' }),
      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submit }, h('span', { html: iconSvg(Plus, 16) }), 'Crear sucursal'),
      ),
    );
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');
    async function submit() {
      const values = {
        name: form.querySelector('[name="name"]').value.trim(),
        department: form.querySelector('[name="department"]').value,
        address: form.querySelector('[name="address"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
      };
      const errors = validateForm(values, branchRules);
      if (Object.keys(errors).length) { showFieldErrors(form, errors); return; }
      const data = {
        name: values.name,
        department: values.department,
        address: values.address || null,
        phone: values.phone || null,
        email: values.email || null,
      };
      try {
        await BranchAPI.create(data);
        toast('Sucursal creada', 'success');
        m.close();
        refresh();
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  function openDetail(branch) {
    const body = h('div', { class: 'muted' }, 'Cargando…');
    const modal = showModal(h('div', {},
      h('h3', {}, branch.name),
      body,
    ));
    modal.close;
    BranchAPI.getDetail(branch.id)
      .then((res) => {
        clear(body);
        const d = res.data;
        body.appendChild(h('div', { class: 'grid grid-2', style: { marginBottom: '12px' } },
          infoField('Departamento', DEPARTMENT_LABELS[d.branch.department] || d.branch.department || '—'),
          infoField('Estado', d.branch.is_active ? 'Activo' : 'Inactivo', d.branch.is_active ? 'active' : 'inactive'),
          infoField('Dirección', d.branch.address || '—'),
          infoField('Teléfono', d.branch.phone || '—'),
          infoField('Correo electrónico', d.branch.email || '—'),
          infoField('Creada', formatDate(d.branch.created_at)),
        ));
        body.appendChild(section('Administradores', d.administrators,
          (a) => h('div', { class: 'row-between' },
            h('div', {},
              h('strong', {}, a.full_name),
              h('div', { class: 'muted text-sm' }, a.email),
            ),
            statusBadgeFor(a.is_active),
          )
        ));
        body.appendChild(section('Instructores', d.instructors,
          (i) => h('div', { class: 'row-between' },
            h('div', {},
              h('strong', {}, i.full_name),
              h('div', { class: 'muted text-sm' },
                i.email
              ),
            ),
            statusBadgeFor(i.is_active),
          )
        ));
        body.appendChild(section('Beneficiarios', d.beneficiaries,
          (b) => h('div', { class: 'row-between' },
            h('div', {},
              h('strong', {}, b.full_name),
              h('div', { class: 'muted text-sm' }, b.email),
            ),
            statusBadgeFor(b.is_active),
          ),
          { countLabel: 'beneficiarios' }
        ));
        body.appendChild(section('Servicios', d.services,
          (s) => h('div', { class: 'row-between' },
            h('div', {},
              h('strong', {}, s.name),
            ),
            statusBadgeFor(s.is_active),
          )
        ));
      })
      .catch((err) => { clear(body); body.appendChild(h('div', { class: 'muted' }, err.message)); });
  }

  function openEdit(branch) {
    const form = h('div', { class: 'form-create' },
      h('h3', {}, `Editar ${branch.name}`),
      h('p', { class: 'muted text-sm' }, 'Modifica los datos de la sucursal.'),
      h('div', { class: 'form-section' },
        h('label', {}, 'Nombre'),
        h('input', { name: 'name', value: branch.name || '', autocomplete: 'organization', required: true }),
        h('label', {}, 'Departamento'),
        h('select', { name: 'department', required: true },
          h('option', { value: '' }, 'Seleccionar…'),
          ...DEPARTMENTS.map((d) => h('option', { value: d.value }, d.label)),
        ),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Dirección'),
            h('input', { name: 'address', value: branch.address || '' }),
          ),
          h('div', {},
            h('label', {}, 'Teléfono'),
            h('input', { name: 'phone', type: 'tel', inputmode: 'numeric', value: branch.phone || '' }),
          ),
        ),
        h('label', {}, 'Correo electrónico'),
        h('input', { name: 'email', type: 'email', value: branch.email || '' }),
        h('label', {}, 'Activo'),
        h('select', { name: 'is_active' },
          h('option', { value: '1' }, 'Activo'),
          h('option', { value: '0' }, 'Inactivo'),
        ),
      ),
      h('div', { class: 'btn-row mt-16', style: { display: 'flex', gap: '8px' } },
        h('button', { class: 'btn btn-danger', style: { flex: '0 0 auto' }, onclick: handleDelete }, h('span', { html: iconSvg(Trash2, 16) }), 'Eliminar'),
        h('button', { class: 'btn btn-block', onclick: submit }, h('span', { html: iconSvg(Check, 16) }), 'Guardar cambios'),
      ),
    );
    // Set select values via IDL property (more reliable than content attributes)
    form.querySelector('[name="department"]').value = branch.department || '';
    form.querySelector('[name="is_active"]').value = branch.is_active ? '1' : '0';
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');
    async function submit() {
      const values = {
        name: form.querySelector('[name="name"]').value.trim(),
        department: form.querySelector('[name="department"]').value,
        address: form.querySelector('[name="address"]').value.trim(),
        phone: form.querySelector('[name="phone"]').value.trim(),
        email: form.querySelector('[name="email"]').value.trim(),
      };
      const errors = validateForm(values, branchRules);
      if (Object.keys(errors).length) { showFieldErrors(form, errors); return; }
      const data = {
        name: values.name,
        department: values.department,
        address: values.address || null,
        phone: values.phone || null,
        email: values.email || null,
        is_active: form.querySelector('[name="is_active"]').value === '1',
      };
      if (branch.is_active && data.is_active === false) {
        const confirmed = await confirmModal(`¿Desactivar la sucursal "${branch.name}"?`, { confirmText: 'Desactivar' });
        if (!confirmed) return;
      }
      try {
        await BranchAPI.update(branch.id, data);
        toast('Sucursal actualizada', 'success');
        m.close();
        refresh();
      } catch (err) { toast(err.message, 'error'); }
    }
    async function handleDelete() {
      const confirmed = await confirmModal(
        `¿Eliminar permanentemente la sucursal "${branch.name}"?\nEsta acción no se puede deshacer.`,
        { confirmText: 'Eliminar', variant: 'danger' }
      );
      if (!confirmed) return;
      try {
        await BranchAPI.del(branch.id);
        toast('Sucursal eliminada', 'success');
        m.close();
        refresh();
      } catch (err) { toast(err.message, 'error'); }
    }
  }
};

const infoField = (label, value, badgeClass) => h('div', { class: 'profile-field' },
  h('label', {}, label),
  badgeClass
    ? h('span', { class: `badge ${badgeClass}` }, value)
    : h('div', { class: 'profile-value' }, value)
);

const statusBadgeFor = (isActive) => h('span', { class: `badge ${isActive ? 'active' : 'inactive'}` }, isActive ? 'Activo' : 'Inactivo');

const section = (title, items, rowRenderer, { countLabel } = {}) => {
  const card = h('div', { class: 'card', style: { marginBottom: '12px' } },
    h('div', { class: 'row-between' },
      h('strong', {}, title),
      h('span', { class: 'muted text-sm' }, `${items.length} ${countLabel || ''}`.trim()),
    ),
  );
  if (!items.length) {
    card.appendChild(h('div', { class: 'muted text-sm mt-8' }, 'Ninguno.'));
  } else {
    for (const it of items) card.appendChild(rowRenderer(it));
  }
  return card;
};
